import DsAuthPrimaryTokenServiceBase from './DsAuthPrimaryTokenServiceBase';
import { Protocol, config } from '../AuthConfig';
import { NetworkLocation, StoreType } from '../TypeDefinitions';
import { AuthChoice } from './data/AuthChoice';
import { GenericRequest, HTTPFetchResponse, PrimaryTokenStatus } from './data/AuthTypes';
import { primaryTokenStatusConstants } from '../AuthConstants';
import { RequestTokenChoicesParser } from './parser/RequestTokenChoicesParser';
import { AuthenticateError, isTokenResponse } from './Authenticators/Authenticator';
import { PrimaryTokenFetchFailure } from '../AuthErrors';
import { ProtocolSelector } from './ProtocolSelector';
import { getAuthenticator } from './AuthenticatorFactory';
import { AotCategory } from '../../utils/Constants';
import { Logger } from '../Logger';

export default class OnPremPrimaryTokenService extends DsAuthPrimaryTokenServiceBase {
	private protocolSelector = new ProtocolSelector();
	constructor(readonly networkLocation: NetworkLocation, readonly storeType: StoreType) {
		super();
	}

	protected async getToken(
		authProtocolsResponse: HTTPFetchResponse,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<PrimaryTokenStatus> {
		const requestTokenChoicesParser = new RequestTokenChoicesParser();
		const serverAuthChoices: AuthChoice[] = requestTokenChoicesParser.parseXML(
			authProtocolsResponse.body
		);
		const usableAuthChoices = this.protocolSelector.getUsableAuthChoices(
			serverAuthChoices,
			this.networkLocation !== NetworkLocation.Inside
		);

		for (const authChoice of usableAuthChoices) {
			const authenticator = getAuthenticator(
				authChoice.getProtocol().toLowerCase() as Protocol
			);
			Logger.getInstance().writeAot(
				AotCategory.Info,
				`Fetching primary token using ${authChoice.getProtocol()} protocol`
			);

			const primaryTokenResponse = await authenticator.getToken(
				authChoice,
				authProtocolsRequest,
				id,
				referenceUri,
				transactionId
			);

			if (!isTokenResponse(primaryTokenResponse)) {
				if (primaryTokenResponse === AuthenticateError.CancelledByUser) {
					Logger.getInstance().writeAot(
						AotCategory.Warning,
						`Authentication cancelled by user or due to timeout`
					);
					return new PrimaryTokenStatus(
						primaryTokenStatusConstants.ERROR_CANCEL_LOGIN,
						true
					);
				} else if (primaryTokenResponse === AuthenticateError.UnsuitableProtocol) {
					continue;
				}

				throw new PrimaryTokenFetchFailure(
					new Error(
						`Unexpected response in getToken:${JSON.stringify(primaryTokenResponse)}`
					)
				);
			} else {
				return await this.validateAndStoreToken(
					primaryTokenResponse,
					referenceUri,
					id,
					'login',
					false
				);
			}
		}

		throw new PrimaryTokenFetchFailure(
			new Error('Failed to fetch primary token using any authenticator.')
		);
	}

	protected getPrimaryTokenLifetime(): string {
		return config.primaryTokenLifetimeOnPrem;
	}
}
